import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    AudioElement,
    ControllableElement,
    MultiPageImageModel,
    MultiPageVideoModel,
} from '@bynder-studio/render-core';
import { List, Thumbnail, TruncatedText } from '@bynder/design-system';
import { IconShow, IconVolumeUp } from '@bynder/icons';
import { useTranslate } from '@bynder/localization';
import generateTestId from 'packages/helpers/testIdHelpers';
import useVisibilityControlsShortcuts from 'packages/hooks/shortcuts/useVisibilityControlsShortcuts';
import { parseShortcutForTooltip } from 'packages/common/WithShortcutLabel';
import MaybeTooltip from 'packages/common/MaybeTooltip';
import NoTooltipWrapper from 'packages/common/NoTooltipWrapper';

type Props = {
    selectedElements: ControllableElement[];
    creativeModel: MultiPageVideoModel | MultiPageImageModel;
    disabled?: boolean;
};

const AllowToggleVisibility = ({ selectedElements, creativeModel, disabled = false }: Props) => {
    const { translate } = useTranslate();
    const unlockedElements = selectedElements.filter((element) => !element.locked);
    const selectedElementsForVisibility =
        selectedElements.length > 1 && unlockedElements.length ? unlockedElements : selectedElements;
    const isAllVisible = selectedElementsForVisibility.every((element) => element.allowToggleVisibility);
    const isAllLocked = unlockedElements.length === 0;
    const [visibility, setVisibility] = useState(false);
    const testId = useMemo(() => generateTestId('shots_properties_visibility_control'), []);

    const isAudioElements =
        selectedElements.length && selectedElements.every((element) => element instanceof AudioElement);

    const onToggle = useCallback(() => {
        if (isAllLocked || disabled) {
            return;
        }

        const newVisibility = !isAllVisible;

        unlockedElements.forEach((element) => {
            creativeModel.updateElement(element.id, { allowToggleVisibility: newVisibility });
        });

        setVisibility(newVisibility);
    }, [creativeModel, disabled, isAllLocked, isAllVisible, unlockedElements]);

    useEffect(() => {
        setVisibility(isAllVisible);
    }, [isAllVisible]);

    useVisibilityControlsShortcuts({ toggleVisibility: onToggle });

    const tooltipText = !isAudioElements
        ? translate('editor.element.visibility_control.tooltip')
        : translate('editor.element.audio.visibility_control.tooltip');
    const hasOverrideTooltip = selectedElements.length > 1 && (!isAllLocked || disabled);
    const hasShortcutTooltip = selectedElements.length === 1 && !disabled;
    const tooltipContent = hasOverrideTooltip
        ? translate('editor.element.multiselect.override.tooltip')
        : `${tooltipText} (${parseShortcutForTooltip(['s'])})`;

    return (
        <MaybeTooltip content={tooltipContent} hasTooltip={hasOverrideTooltip || hasShortcutTooltip}>
            <NoTooltipWrapper>
                <List.Item
                    checkedVariant="switch"
                    onClick={onToggle}
                    isChecked={visibility}
                    isDisabled={isAllLocked || disabled}
                    thumbnail={<Thumbnail icon={isAudioElements ? <IconVolumeUp /> : <IconShow />} />}
                    {...testId}
                >
                    <TruncatedText>
                        {isAudioElements
                            ? translate('editor.sidebar.shots.audio_control.label')
                            : translate('editor.sidebar.shots.visibility_control.label')}
                    </TruncatedText>
                </List.Item>
            </NoTooltipWrapper>
        </MaybeTooltip>
    );
};

export default AllowToggleVisibility;
